import { useState, useEffect } from 'react'
import { useLatest } from '../useLatest'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { fetchStaatidForhandlere, fetchStaatidMerker, UNKNOWN_ERROR } from './api'
import type { StaatidParams, StaatidForhandlereResponse, ForhandlerSort } from './api'
import ForhandlerTable from './ForhandlerTable'
import ForhandlerDetalj from './ForhandlerDetalj'
import StaatidFilterBar, { EMPTY_LOCAL_FILTERS } from './StaatidFilterBar'
import type { StaatidLocalFilters } from './StaatidFilterBar'
import MultiSelectTypeahead from './MultiSelectTypeahead'
import FilterSlot, { useLocalFilter } from '../FilterSlot'

const LIMIT = 50
const DEBOUNCE_MS = 300

export default function ForhandlereTab() {
  const { t } = useTranslation('staatid')
  const { from, to, selgerPostnummer } = useBruktbilFilters()
  // #529 AC6 — the tab had no ståtid filters at all, so the antall-ledd and
  // merke choices the rest of the module offers never reached either the
  // dealer grid or the detail page's trend. Both now read the SAME applied
  // values, so the numbers on the two screens are about the same rows.
  const [localDraft, setLocalDraft, localFilters] =
    useLocalFilter<StaatidLocalFilters>('staatid', EMPTY_LOCAL_FILTERS)
  const [merkeDraft, setMerkeDraft, merke] = useLocalFilter<string[]>('staatidMerke', [])
  const [merkeOptions, setMerkeOptions] = useState<string[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  // Detail drill lives in the URL so it is deep-linkable: ?forhandler=<org_nr>
  const forhandler = searchParams.get('forhandler')

  const [data, setData] = useState<StaatidForhandlereResponse | null>(null)
  const [sort, setSort] = useState<ForhandlerSort>('antall_roundtrips')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('') // debounced value actually sent
  const [minAntallInput, setMinAntallInput] = useState('')
  const [minAntall, setMinAntall] = useState<number | undefined>(undefined) // debounced
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const baseFilters: StaatidParams = {
    from, to, selgerPostnummer, ...localFilters,
    ...(merke.length ? { merke } : {}),
  }
  const filterKey =
    `${from}|${to}|${selgerPostnummer ?? ''}|${JSON.stringify(localFilters)}|${merke.join(',')}`

  // Merke options for the period, by volume — same source SporringTab uses.
  useLatest(alive => {
    fetchStaatidMerker({ from, to, selgerPostnummer }, [], 500, 0)
      .then(m => {
        if (alive()) setMerkeOptions(m.data.rows.map(r => r.merke).filter((x): x is string => !!x))
      })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [from, to, selgerPostnummer])

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search)
      const n = parseInt(minAntallInput, 10)
      setMinAntall(Number.isFinite(n) && n >= 1 ? n : undefined)
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, minAntallInput])

  useLatest(alive => {
    if (forhandler) return // detail view fetches its own data
    setLoading(true)
    setError(null)
    fetchStaatidForhandlere(baseFilters, sort, sortDir, LIMIT, offset, q, minAntall)
      .then(f => { if (alive()) setData(f.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [filterKey, sort, sortDir, offset, q, minAntall, forhandler])

  function openDetail(orgNr: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('forhandler', orgNr)
      return next
    })
  }

  function closeDetail() {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('forhandler')
      return next
    })
  }

  // Rendered on the detail view too: the filters drive the dealer's trend
  // chart there, so hiding them on drill-down would make the chart unfilterable.
  const filterControls = (
    <FilterSlot>
      <div className="flex flex-wrap gap-x-6 gap-y-3 items-end">
        <StaatidFilterBar value={localDraft} onChange={setLocalDraft} />
        <MultiSelectTypeahead
          label={t('common.merke')}
          placeholder={t('common.searchMerke')}
          options={merkeOptions}
          selected={merkeDraft}
          onChange={setMerkeDraft}
        />
      </div>
    </FilterSlot>
  )

  if (forhandler) {
    return (
      <>
        {filterControls}
        <ForhandlerDetalj orgNr={forhandler} baseFilters={baseFilters} onBack={closeDetail} />
      </>
    )
  }

  if (error) {
    return (
      <>
        {filterControls}
        <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
          {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
        </div>
      </>
    )
  }

  return (
    <div className="space-y-4">
      {filterControls}
      <div className="card p-3 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          data-testid="staatid-forhandler-search"
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            setOffset(0)
          }}
          placeholder={t('forhandlere.searchPlaceholder')}
          className="w-full sm:w-96 text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-3 py-1.5"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          {t('forhandlere.minAntall')}
          <input
            type="number"
            min={1}
            data-testid="staatid-min-antall"
            value={minAntallInput}
            onChange={e => {
              setMinAntallInput(e.target.value)
              setOffset(0)
            }}
            placeholder={t('forhandlere.minAntallPlaceholder')}
            className="w-20 text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1.5"
          />
        </label>
      </div>
      <ForhandlerTable
        rows={data?.rows ?? []}
        total={data?.total ?? 0}
        offset={offset}
        limit={LIMIT}
        sort={sort}
        sortDir={sortDir}
        onSortChange={next => {
          setSortDir(next === sort && sortDir === 'desc' ? 'asc' : 'desc')
          setSort(next)
          setOffset(0)
        }}
        onPageChange={setOffset}
        onRowClick={openDetail}
        loading={loading}
        exportMeta={{ dateRange: `${from} – ${to}` }}
      />
    </div>
  )
}
